/* InteractiveBook — Ancient Luxury Heirloom redesign (iter95.9i)

Two-stage experience:
  1. Closed state — a hardcover black leather tome with embossed gold
     ornament + portrait + QR sits centered. Tap/click opens it.
  2. Open state  — react-pageflip mounts and reveals parchment pages
     with darkened edges, decorative gold frames, sepia photo inserts,
     handwritten-style family messages, and chapter dividers.

Sourced from the PUBLIC slug-based preview endpoint so a QR scan on
another device opens the book directly without auth. */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import HTMLFlipBook from 'react-pageflip';
import { ArrowLeft, ChevronLeft, ChevronRight, Volume2, VolumeX } from 'lucide-react';
import { api, formatApiError, BACKEND_URL } from '../lib/api';
import { toast } from 'sonner';
import './InteractiveBook.css';

// ----- helpers ---------------------------------------------------------------
function bookUrl(slug) {
  return `${window.location.origin}/m/${slug}/book`;
}
function memorialUrl(slug) {
  return `${window.location.origin}/m/${slug}`;
}
function qrSrc(slug, target = 'book', style = 'plaque', size = 420) {
  const params = new URLSearchParams({ target, style, size: String(size) });
  return `${BACKEND_URL}/api/public/${encodeURIComponent(slug)}/qr.png?${params.toString()}`;
}
function photoUrl(mediaId) {
  return `${BACKEND_URL}/api/media/${mediaId}/file`;
}
function profileBg(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
}

// ----- SVG ornaments ---------------------------------------------------------
function OrnamentCorner() {
  // Pure inner-page corner flourish — light filigree for parchment pages.
  return (
    <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="gold-corner" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#f3d989" />
          <stop offset="50%" stopColor="#c8a558" />
          <stop offset="100%" stopColor="#8b6a2b" />
        </linearGradient>
      </defs>
      <g fill="none" stroke="url(#gold-corner)" strokeWidth="1.4" strokeLinecap="round">
        <path d="M 4 22 Q 4 4 22 4" />
        <path d="M 4 32 Q 4 14 24 12 Q 30 11 32 4" />
        <path d="M 12 8 Q 16 14 22 14 Q 24 22 18 22 Q 14 22 12 18" />
        <circle cx="22" cy="22" r="2" fill="url(#gold-corner)" stroke="none" />
        <path d="M 22 22 Q 30 26 32 32" />
      </g>
    </svg>
  );
}

// Heavy 3D brass corner-bracket for the closed hardcover — emulates an
// antique book's metal corner hardware. Two layers (shadow + face) sell the
// brass depth without a raster image.
function BrassCorner() {
  return (
    <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="brass-face" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"  stopColor="#fff1c9" />
          <stop offset="22%" stopColor="#e9c479" />
          <stop offset="55%" stopColor="#a47628" />
          <stop offset="85%" stopColor="#6c4814" />
          <stop offset="100%" stopColor="#3a2509" />
        </linearGradient>
        <linearGradient id="brass-bevel" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff7d5" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#5a3c0e" stopOpacity="0" />
        </linearGradient>
        <radialGradient id="brass-stud" cx="50%" cy="40%" r="60%">
          <stop offset="0%"  stopColor="#fff3c0" />
          <stop offset="55%" stopColor="#b78538" />
          <stop offset="100%" stopColor="#3d2607" />
        </radialGradient>
      </defs>
      {/* drop shadow */}
      <path d="M 4 4 L 64 4 L 64 14 L 14 14 L 14 64 L 4 64 Z" fill="rgba(0,0,0,0.55)" />
      {/* main brass face */}
      <path d="M 2 2 L 62 2 L 62 12 L 12 12 L 12 62 L 2 62 Z" fill="url(#brass-face)" />
      {/* top edge highlight */}
      <path d="M 2 2 L 62 2 L 60 4 L 4 4 Z" fill="url(#brass-bevel)" />
      <path d="M 2 2 L 4 4 L 4 60 L 2 62 Z" fill="url(#brass-bevel)" />
      {/* decorative rivet */}
      <circle cx="8" cy="8" r="2.6" fill="url(#brass-stud)" />
      {/* small inner filigree on the inside curve */}
      <path d="M 14 14 Q 24 18 26 24 Q 22 30 16 28 Q 14 24 14 14 Z"
            fill="none" stroke="#3a2509" strokeWidth="0.6" opacity="0.55" />
    </svg>
  );
}

// MyLeaf wordmark + leaf for the top of the closed cover.
function MyLeafLogo() {
  return (
    <div className="ml-tome-myleaf">
      <svg className="ml-tome-myleaf-icon" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <defs>
          <linearGradient id="ml-leaf-gold" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#f3d989" />
            <stop offset="60%" stopColor="#c8a558" />
            <stop offset="100%" stopColor="#7a5a20" />
          </linearGradient>
        </defs>
        {/* simple olive leaf */}
        <path d="M 16 4 C 10 8 6 14 8 22 C 14 22 20 18 22 12 C 22 8 19 5 16 4 Z"
              fill="url(#ml-leaf-gold)" />
        <path d="M 16 6 Q 14 14 10 20" fill="none" stroke="#3a2509"
              strokeWidth="0.7" opacity="0.55" />
        {/* stem */}
        <path d="M 8 22 L 6 28" stroke="url(#ml-leaf-gold)" strokeWidth="1.6"
              strokeLinecap="round" />
      </svg>
      <span className="ml-tome-myleaf-text">MyLeaf</span>
    </div>
  );
}

// Long-form memorial dates: "March 14, 1950 – May 20, 2023"
const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];
function formatLongDate(s) {
  if (!s) return '';
  // ISO 'YYYY-MM-DD' or 'YYYY-MM-DDT…' — split on date only to avoid TZ shift.
  const m = String(s).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  const [, y, mo, d] = m;
  return `${MONTHS[parseInt(mo, 10) - 1]} ${parseInt(d, 10)}, ${y}`;
}

// ----- page primitives -------------------------------------------------------
const Page = React.forwardRef(function Page({ children, side, pageNumber, className = '' }, ref) {
  return (
    <div ref={ref} className={`ml-tome-page ml-tome-page--${side} ${className}`}>
      <div className="ml-tome-page-frame" aria-hidden />
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: '1 1 auto' }}>
        {children}
      </div>
      {pageNumber != null && <div className="ml-tome-pageno">— {pageNumber} —</div>}
    </div>
  );
});

// First and last "endpaper" pages — when StPageFlip uses showCover, the FIRST
// and LAST child get rendered as hardcovers. We use this for the closing
// inside-cover so the book has a back endpaper too.
const HardCoverInner = React.forwardRef(function HardCoverInner(_, ref) {
  return (
    <div ref={ref} className="ml-tome-cover-page" data-density="hard"
         style={{
           background: 'radial-gradient(ellipse at center, #1a0e08 0%, #0a0503 100%)',
           color: '#d9bf85',
         }}>
      <div className="ml-tome-corner ml-tome-corner--tl"><OrnamentCorner /></div>
      <div className="ml-tome-corner ml-tome-corner--tr"><OrnamentCorner /></div>
      <div className="ml-tome-corner ml-tome-corner--bl"><OrnamentCorner /></div>
      <div className="ml-tome-corner ml-tome-corner--br"><OrnamentCorner /></div>
      <div style={{
        margin: 'auto', textAlign: 'center', maxWidth: '72%',
        fontFamily: '"Cinzel", serif', fontStyle: 'normal', fontSize: 14,
        letterSpacing: '0.32em', textTransform: 'uppercase',
        color: '#d9bf85', lineHeight: 1.8,
      }}>
        A Memory Book<br/>
        <span style={{ fontSize: 9, letterSpacing: '0.5em', color: 'rgba(217,191,133,0.6)' }}>
          MYLEAF LEGACY · FOREVER HERE
        </span>
      </div>
    </div>
  );
});

const Dedication = React.forwardRef(function Dedication({ name, message, quote, attribution, pageNumber, side }, ref) {
  return (
    <Page ref={ref} side={side} pageNumber={pageNumber} className="ml-tome-dedication">
      <div className="ml-tome-overline">In Loving Memory Of</div>
      <h2 className="ml-tome-dedication-name">{name}</h2>
      <div className="ml-tome-hairline" />
      {message && <p className="ml-tome-dedication-msg">{message}</p>}
      {quote && <p className="ml-tome-dedication-quote">“{quote}”</p>}
      {attribution && <p className="ml-tome-dedication-attr">— {attribution}</p>}
    </Page>
  );
});

const ChapterDivider = React.forwardRef(function ChapterDivider({ chapterNumber, chapterTitle, pageNumber, side }, ref) {
  return (
    <Page ref={ref} side={side} pageNumber={pageNumber} className="ml-tome-chapter">
      <div className="ml-tome-chapter-no">Chapter · {String(chapterNumber).padStart(2, '0')}</div>
      <div className="ml-tome-chapter-title">{chapterTitle}</div>
    </Page>
  );
});

const HeroPhoto = React.forwardRef(function HeroPhoto({ photo, pageNumber, side }, ref) {
  return (
    <Page ref={ref} side={side} pageNumber={pageNumber}>
      <div className="ml-tome-overline">A Memory</div>
      <div className="ml-tome-hero-frame">
        <div className="ml-tome-hero-photo"
             style={{ backgroundImage: `url(${photoUrl(photo.media_id)})` }} />
      </div>
      {photo.title && <h3 className="ml-tome-hero-title">{photo.title}</h3>}
      {photo.meta_line && <div className="ml-tome-hero-meta">{photo.meta_line}</div>}
      {photo.caption && <p className="ml-tome-hero-caption">{photo.caption}</p>}
      {photo.contributor && <div className="ml-tome-credit">— Recounted by {photo.contributor}</div>}
    </Page>
  );
});

const Moment = React.forwardRef(function Moment({ photo, pageNumber, side }, ref) {
  return (
    <Page ref={ref} side={side} pageNumber={pageNumber}>
      <div className="ml-tome-overline">A Moment</div>
      <div className="ml-tome-moment-frame">
        <div className="ml-tome-moment-photo"
             style={{ backgroundImage: `url(${photoUrl(photo.media_id)})` }} />
      </div>
      {photo.title && <h3 className="ml-tome-moment-title">{photo.title}</h3>}
      {photo.meta_line && <div className="ml-tome-moment-meta">{photo.meta_line}</div>}
      {photo.caption && <p className="ml-tome-moment-caption">{photo.caption}</p>}
      {photo.contributor && <div className="ml-tome-credit">— {photo.contributor}</div>}
    </Page>
  );
});

const FamilyMessage = React.forwardRef(function FamilyMessage({ msg, pageNumber, side }, ref) {
  return (
    <Page ref={ref} side={side} pageNumber={pageNumber} className="ml-tome-message">
      <div className="ml-tome-overline">From the Family</div>
      <div className="ml-tome-hairline" />
      <p className="ml-tome-message-quote">{msg.message}</p>
      <div className="ml-tome-message-name">{msg.name}</div>
      {msg.relationship && <div className="ml-tome-message-rel">{msg.relationship}</div>}
    </Page>
  );
});

const Closing = React.forwardRef(function Closing({ name, slug, pageNumber, side }, ref) {
  return (
    <Page ref={ref} side={side} pageNumber={pageNumber} className="ml-tome-closing">
      <div className="ml-tome-overline">In Loving Memory</div>
      <div className="ml-tome-hairline" />
      <p className="ml-tome-closing-line">Forever Here</p>
      <div className="ml-tome-closing-name">{name}</div>
      <img className="ml-tome-closing-qr" src={qrSrc(slug, 'memorial')} alt="Visit memorial online" />
      <div className="ml-tome-closing-url">{memorialUrl(slug)}</div>
    </Page>
  );
});

// ============== Closed Hardcover ============================================
function ClosedCover({ memorial, slug, onOpen, isOpening }) {
  const portraitBg = profileBg(memorial.profile_photo_url);
  const dob = formatLongDate(memorial.date_of_birth);
  const dod = formatLongDate(memorial.date_of_passing);
  const datesLine = (dob || dod) ? `${dob || '—'}  –  ${dod || 'present'}` : null;
  const honorific = (() => {
    // Build "Scan to open and read his/her/their story" from gender if present.
    const g = (memorial.gender || '').toLowerCase();
    if (g === 'male')   return 'his';
    if (g === 'female') return 'her';
    return 'their';
  })();

  return (
    <div className={`ml-tome-closed-wrap ${isOpening ? 'is-opening' : ''}`}>
      <div className="ml-tome-closed" onClick={onOpen} role="button" tabIndex={0}
           onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpen(); }}
           data-testid="closed-book-cover" aria-label="Open the memory book">
        <div className="ml-tome-spine" />
        <div className="ml-tome-corner ml-tome-corner--tl"><BrassCorner /></div>
        <div className="ml-tome-corner ml-tome-corner--tr"><BrassCorner /></div>
        <div className="ml-tome-corner ml-tome-corner--bl"><BrassCorner /></div>
        <div className="ml-tome-corner ml-tome-corner--br"><BrassCorner /></div>

        <MyLeafLogo />
        <h1 className="ml-tome-title">Memory Book</h1>

        <div className="ml-tome-portrait-frame">
          <div className="ml-tome-portrait"
               style={portraitBg ? { backgroundImage: `url(${portraitBg})` } : undefined} />
        </div>

        <h2 className="ml-tome-name">{memorial.full_name}</h2>
        <div className="ml-tome-name-rule">
          <span className="ml-tome-name-rule-line" />
          <span className="ml-tome-name-rule-flourish">❦</span>
          <span className="ml-tome-name-rule-line" />
        </div>
        {datesLine && <div className="ml-tome-dates">{datesLine}</div>}

        <div className="ml-tome-qr-section" data-testid="cover-qr-section">
          <div className="ml-tome-qr-frame">
            <img
              className="ml-tome-qr"
              src={qrSrc(slug, 'book', 'clean', 720)}
              alt="QR code — scan to open this Memory Book"
              data-testid="cover-qr-image"
            />
          </div>
          <div className="ml-tome-qr-caption">Scan to open and read {honorific} story</div>
        </div>
        <div className="ml-tome-cta">— Tap to open —</div>
      </div>
    </div>
  );
}

// ============== Main component ==============================================
const SOUND_PREF_KEY = 'mlBookPageTurnSound';

export default function InteractiveBook() {
  const { slug, memorialId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const flipRef = useRef(null);
  const audioCtxRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opened, setOpened] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [soundOn, setSoundOn] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SOUND_PREF_KEY) === '1';
  });
  // Keep a ref alongside the state so the onFlip callback (memoised once)
  // always reads the latest user preference without rebuilding.
  const soundOnRef = useRef(soundOn);
  useEffect(() => { soundOnRef.current = soundOn; }, [soundOn]);

  const identifier = slug || memorialId;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Public preview by slug — no auth required (works on a fresh
        // device that scanned the cover QR).
        const { data: preview } = await api.get(`/public/memorials/${identifier}/book/preview`);
        if (!cancelled) setData(preview);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [identifier]);

  // Synthesise a short paper-rustle on each page turn via the Web Audio
  // API — avoids shipping any audio asset and works offline. The sound
  // is built from a short burst of band-pass-filtered white noise with a
  // gentle exponential decay, which our ear reads as "paper".
  const playPageTurn = useCallback(() => {
    if (!soundOnRef.current) return;
    if (typeof window === 'undefined') return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
      const ctx = audioCtxRef.current;
      // Safari resumes only after a user gesture — flip is a gesture so it's fine.
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});

      const duration = 0.38;
      const sampleRate = ctx.sampleRate;
      const len = Math.floor(duration * sampleRate);
      const noise = ctx.createBuffer(1, len, sampleRate);
      const chan = noise.getChannelData(0);
      for (let i = 0; i < len; i++) {
        // White noise with a fast attack + exponential decay envelope.
        const t = i / len;
        const env = Math.exp(-3.2 * t) * (1 - t * 0.4);
        chan[i] = (Math.random() * 2 - 1) * env;
      }
      const src = ctx.createBufferSource();
      src.buffer = noise;

      // Band-pass focuses the energy in the 1.5–5 kHz "paper" range.
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 2400;
      bp.Q.value = 0.9;

      // High-shelf cut so it never sounds like a hiss.
      const hs = ctx.createBiquadFilter();
      hs.type = 'highshelf';
      hs.frequency.value = 6000;
      hs.gain.value = -12;

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.22, ctx.currentTime + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);

      src.connect(bp);
      bp.connect(hs);
      hs.connect(gain);
      gain.connect(ctx.destination);
      src.start();
      src.stop(ctx.currentTime + duration + 0.05);
    } catch {
      // Audio is a nice-to-have — never let it break the flip itself.
    }
  }, []);

  const onFlip = useCallback((e) => {
    setCurrentPage(e.data);
    playPageTurn();
  }, [playPageTurn]);
  const flipPrev = () => flipRef.current?.pageFlip()?.flipPrev();
  const flipNext = () => flipRef.current?.pageFlip()?.flipNext();
  const toggleSound = () => {
    setSoundOn((v) => {
      const next = !v;
      try { window.localStorage.setItem(SOUND_PREF_KEY, next ? '1' : '0'); } catch (_e) { /* ignore quota / private-mode errors */ }
      return next;
    });
  };

  const openBook = useCallback(() => {
    if (isOpening) return;
    setIsOpening(true);
    // Match the 1100ms cover-pivot animation in CSS. The flipbook is
    // mounted IMMEDIATELY (the moment isOpening flips true the render
    // enters the opened-path with the ClosedCover overlaying the
    // already-mounted flipbook). When the pivot completes at 1100ms,
    // we drop the overlay — no blank gap, no hard cut.
    setTimeout(() => setOpened(true), 1100);
  }, [isOpening]);

  // Keyboard nav once the book is open
  useEffect(() => {
    if (!opened) return undefined;
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'PageDown') flipNext();
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') flipPrev();
      else if (e.key === 'Escape') navigate(-1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [opened, navigate]);

  const pages = useMemo(() => {
    if (!data) return null;
    const memorial = data.memorial;
    const settings = data.settings || {};
    const messages = settings.family_messages || [];
    const story = data.pages || [];
    const dedication = {
      kind: 'dedication',
      message: settings.dedication_message || '',
      quote: settings.dedication_quote || '',
      attribution: settings.dedication_quote_attribution || '',
    };
    const items = [dedication, ...story, ...messages.map((m) => ({ kind: 'family_message', msg: m })), { kind: 'closing' }];
    return { memorial, settings, items };
  }, [data]);

  // Build page elements ONCE per data load — react-pageflip resets to
  // startPage={0} whenever its children prop identity changes. If we
  // rebuilt pageEls inline on every render, each onFlip → setCurrentPage
  // would hand a NEW children array to HTMLFlipBook, collapsing the
  // in-flight flip and reverting to the cover. MUST stay at the top of
  // the component, above any conditional returns, to satisfy Rules of Hooks.
  const pageEls = useMemo(() => {
    if (!pages?.memorial) return [];
    const { memorial, items } = pages;
    const useSlug = memorial.slug;
    const els = [];
    let pageNumber = 1;

    els.push(<HardCoverInner key="endpaper-front" />);

    items.forEach((it, idx) => {
      const side = idx % 2 === 0 ? 'right' : 'left';
      if (it.kind === 'dedication') {
        els.push(
          <Dedication key={`ded-${idx}`} name={memorial.full_name}
            message={it.message} quote={it.quote} attribution={it.attribution}
            pageNumber={pageNumber++} side={side} />,
        );
      } else if (it.kind === 'chapter_divider') {
        els.push(
          <ChapterDivider key={`ch-${idx}`} chapterNumber={it.chapter_number}
            chapterTitle={it.chapter_title} pageNumber={pageNumber++} side={side} />,
        );
      } else if (it.kind === 'hero') {
        els.push(
          <HeroPhoto key={`hero-${idx}`} photo={it}
            pageNumber={pageNumber++} side={side} />,
        );
      } else if (it.kind === 'moment_pair') {
        (it.moments || []).forEach((m, mIdx) => {
          const mSide = (idx + mIdx) % 2 === 0 ? 'right' : 'left';
          els.push(
            <Moment key={`mom-${idx}-${mIdx}`} photo={m}
              pageNumber={pageNumber++} side={mSide} />,
          );
        });
      } else if (it.kind === 'family_message') {
        els.push(
          <FamilyMessage key={`msg-${idx}`} msg={it.msg}
            pageNumber={pageNumber++} side={side} />,
        );
      } else if (it.kind === 'closing') {
        els.push(
          <Closing key="closing" name={memorial.full_name} slug={useSlug}
            pageNumber={pageNumber++} side={side} />,
        );
      }
    });

    els.push(<HardCoverInner key="endpaper-back" />);
    return els;
  }, [pages]);

  if (loading) {
    return (
      <div className="ml-tome-stage">
        <div className="ml-tome-loading">Preparing the book…</div>
      </div>
    );
  }
  if (!data || !pages?.memorial) {
    return (
      <div className="ml-tome-stage">
        <div className="ml-tome-loading">This memory book could not be opened.</div>
      </div>
    );
  }

  const { memorial, settings, items } = pages;
  const useSlug = memorial.slug;

  // End-of-book "Close the book" — returns the reader to wherever they
  // came from (dashboard / overview). A fresh QR-scan device has no
  // history, so fall back to the public memorial overview.
  const closeBook = () => {
    if (window.history.length > 2) navigate(-1);
    else navigate(`/m/${useSlug}`);
  };
  const readAgain = () => {
    flipRef.current?.pageFlip()?.turnToPage(1);
    setCurrentPage(1);
  };

  // ----- closed / opening / opened render -----------------------------------
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 720;
  const pageW = isMobile ? 320 : 460;
  const pageH = isMobile ? 480 : 640;

  // Stay in the ClosedCover render for the full 1.1s hardcover pivot. The
  // cover swings away with `is-opening` applied, reaches opacity 0 at the
  // end, and only THEN do we swap to the spread render — which mounts
  // already-open on the first content spread (startPage={1}) and plays
  // its own arrival animation, so the hand-off reads as a continuous
  // "cover opened → spread revealed" gesture.
  if (!opened) {
    return (
      <div className="ml-tome-stage" data-testid="interactive-book">
        <div className="ml-tome-topbar">
          <button onClick={() => navigate(-1)} className="ml-tome-back" data-testid="book-back-btn">
            <ArrowLeft size={11} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
            Back
          </button>
          <div className="ml-tome-titlebar">A Memory Book</div>
          <div style={{ width: 60 }} />
        </div>
        <ClosedCover
          memorial={memorial}
          slug={useSlug}
          dedication={settings.dedication_message}
          onOpen={openBook}
          isOpening={isOpening}
        />
      </div>
    );
  }

  // Visible "thickness" of pages on each side grows / shrinks as the
  // reader progresses through the book — the LEFT stack starts thin
  // (no pages turned yet) and grows; the RIGHT stack starts thick and
  // shrinks. Total thickness stays constant so the book never looks
  // like it's changing weight.
  const totalLeaves = Math.max(pageEls.length, 2);
  const progress = Math.min(Math.max(currentPage / (totalLeaves - 1), 0), 1);
  const MAX_THICK = isMobile ? 22 : 38;
  const MIN_THICK = 6;
  const leftThick  = Math.round(MIN_THICK + (MAX_THICK - MIN_THICK) * progress);
  const rightThick = Math.round(MIN_THICK + (MAX_THICK - MIN_THICK) * (1 - progress));

  return (
    <div className="ml-tome-stage" data-testid="interactive-book">
      <div className="ml-tome-topbar">
        <button onClick={() => navigate(-1)} className="ml-tome-back" data-testid="book-back-btn">
          <ArrowLeft size={11} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
          Back
        </button>
        <div className="ml-tome-titlebar">{memorial.full_name} · Memory Book</div>
        <button
          onClick={toggleSound}
          className="ml-tome-back"
          data-testid="book-sound-toggle"
          aria-label={soundOn ? 'Mute page-turn sound' : 'Enable page-turn sound'}
          title={soundOn ? 'Page-turn sound: ON' : 'Page-turn sound: OFF'}
          style={{ minWidth: 60 }}
        >
          {soundOn
            ? <><Volume2 size={11} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} /> Sound</>
            : <><VolumeX size={11} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} /> Silent</>}
        </button>
      </div>

      <div className="ml-tome-spread-wrap">
        <div className="ml-tome-stack-l" aria-hidden style={{ width: `${leftThick}px`, left: `-${leftThick - 4}px` }} />
        <div className="ml-tome-stack-r" aria-hidden style={{ width: `${rightThick}px`, right: `-${rightThick - 4}px` }} />
        <div className="ml-tome-gutter" aria-hidden />
        <HTMLFlipBook
          ref={flipRef}
          width={pageW}
          height={pageH}
          size="stretch"
          minWidth={280}
          maxWidth={700}
          minHeight={420}
          maxHeight={960}
          showCover={true}
          maxShadowOpacity={0.55}
          drawShadow={true}
          flippingTime={950}
          mobileScrollSupport={true}
          useMouseEvents={true}
          startPage={1}
          autoSize={true}
          usePortrait={isMobile}
          className="ml-tome-flipbook"
          onFlip={onFlip}
          data-testid="book-flipbook"
        >
          {pageEls}
        </HTMLFlipBook>
      </div>

      {/* End-of-book actions — once the reader reaches the back cover,
          give them an explicit way out instead of a dead end. */}
      {currentPage >= pageEls.length - 1 && (
        <div className="ml-tome-end-actions" data-testid="book-end-actions">
          <div className="ml-tome-end-line">— The End —</div>
          <div className="ml-tome-end-btns">
            <button onClick={readAgain} data-testid="book-read-again-btn">
              Read again
            </button>
            <button className="ml-tome-end-primary" onClick={closeBook} data-testid="book-close-btn">
              Close the book
            </button>
          </div>
        </div>
      )}

      <div className="ml-tome-controls">
        <button onClick={flipPrev} disabled={currentPage === 0}
                data-testid="book-prev-btn" aria-label="Previous page">
          <ChevronLeft size={13} /> Prev
        </button>
        <div className="ml-tome-progress" data-testid="book-progress">
          {currentPage === 0
            ? 'Front cover'
            : currentPage >= pageEls.length - 1
              ? 'Back cover'
              : `Folio ${currentPage} of ${pageEls.length - 2}`}
        </div>
        <button onClick={flipNext} disabled={currentPage >= pageEls.length - 1}
                data-testid="book-next-btn" aria-label="Next page">
          Next <ChevronRight size={13} />
        </button>
      </div>
    </div>
  );
}
